import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";
import { UnauthorizedError, SessionExpiredError, ForbiddenError } from "../domain/errors/errors";
import { CLIENT_HEADERS } from "@config/client";
import { toApiError } from "./to-api-error";

/**
 * A variant of HttpGateway for token-based authentication.
 *
 * Unlike HttpGateway which uses TokenStorageGateway for Bearer tokens and registers
 * with HttpGateways for global token refresh, this class:
 * - Takes an access token directly in constructor
 * - Sets a custom header (default: x-external-provider-token)
 * - Does NOT register with HttpGateways (no global token refresh needed)
 * - Includes same error handling interceptors (401/403/440, then ApiError for the API error contract)
 */
export abstract class TokenBasedHttpGateway {
  protected _instance: AxiosInstance;

  constructor(baseURL: string, token: string, headerName: string = "x-external-provider-token") {
    this._instance = this.createInstance(baseURL, token, headerName);
  }

  private createInstance(baseURL: string, token: string, headerName: string): AxiosInstance {
    const instance = axios.create({
      baseURL,
      headers: { ...CLIENT_HEADERS, [headerName]: token },
    });
    this.addErrorInterceptor(instance);
    return instance;
  }

  private addErrorInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(
      res => res,
      async (error: AxiosError) => {
        if (error.response?.status === 403) {
          throw new ForbiddenError();
        }
        if (error.response?.status === 401) {
          throw new UnauthorizedError();
        }
        if (error.response?.status === 440) {
          throw new SessionExpiredError();
        }
        throw toApiError(error);
      },
    );
  }
}
